// client/src/pages/DataAnalytics/components/RegionalComparison.js
// Jämför symptomfördelning mellan 2-5 städer som stapeldiagram. OBS:
// "Regioner" här är hårdkodade svenska städer, inte faktiska regioner.
import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ALL_SYMPTOMS } from '../../../components/shared/constants/symptoms';
import API_CONFIG from '../../../config/apiConfig';
import './RegionalComparison.css';

// Available Swedish cities for comparison
const AVAILABLE_CITIES = [
  'Stockholm', 'Göteborg', 'Malmö', 'Uppsala', 'Linköping',
  'Örebro', 'Västerås', 'Helsingborg', 'Norrköping', 'Jönköping',
  'Lund', 'Umeå', 'Gävle', 'Sundsvall', 'Karlstad'
];

// Color palette for cities (iOS-inspired)
const CITY_COLORS = [
  '#64d2ff', // Cyan
  '#bf5af2', // Purple
  '#ff9f0a', // Orange
  '#30d158', // Green
  '#ff453a', // Red
];

const RegionalComparison = () => {
  const { t } = useTranslation();

  // ✅ Symptom translation helper
  const tSymptom = (name) => t(`symptoms.${name}`, name);

  // State
  const [selectedCities, setSelectedCities] = useState(['Stockholm', 'Göteborg', 'Malmö']);
  const [selectedSymptoms, setSelectedSymptoms] = useState([]);
  const [weekRange, setWeekRange] = useState({ start: 49, end: 53 });
  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastAnalysis, setLastAnalysis] = useState(null);

  // Available weeks
  const availableWeeks = useMemo(() => 
    Array.from({ length: 14 }, (_, i) => 40 + i), []
  );

  // Toggle city selection
  const handleCityToggle = useCallback((city) => {
    setSelectedCities(prev => {
      if (prev.includes(city)) {
        // Don't allow less than 2 cities
        if (prev.length <= 2) return prev;
        return prev.filter(c => c !== city);
      }
      // Max 5 cities
      if (prev.length >= 5) return prev;
      return [...prev, city];
    });
  }, []);

  // Toggle symptom selection
  const handleSymptomToggle = useCallback((symptom) => {
    setSelectedSymptoms(prev => {
      if (prev.includes(symptom)) {
        return prev.filter(s => s !== symptom);
      }
      return [...prev, symptom];
    });
  }, []);

  // Execute comparison
  const executeComparison = useCallback(async () => {
    if (selectedCities.length < 2) {
      setError(t('regionalComparison.errorMinCities'));
      return;
    }
    
    if (selectedSymptoms.length === 0) {
      setError(t('regionalComparison.errorNoSymptoms'));
      return;
    }

    setLoading(true);
    setError(null);
    setComparisonData(null);

    const startTime = Date.now();
    const symptomsParam = selectedSymptoms.join(',');

    try {
      // Ett /trend-anrop per stad istället för städer × veckor postal-groups-anrop.
      // Servern aggregerar hela veckointervallet i ett svar.
      const cityPromises = selectedCities.map(async (city) => {
        const url = `${API_CONFIG.baseUrl}/health-data/trend?startWeek=${weekRange.start}&endWeek=${weekRange.end}&scope=city&city=${encodeURIComponent(city)}&symptoms=${encodeURIComponent(symptomsParam)}`;

        let weeks = [];
        try {
          const response = await fetch(url);
          const result = await response.json();
          if (result.success && Array.isArray(result.data)) {
            weeks = result.data
              .slice()
              .sort((a, b) => a.week - b.week)
              .map(d => ({ week: d.week, count: d.matchingReports || 0 }));
          }
        } catch {
          weeks = [];
        }

        // Fyll i saknade veckor med 0 så stapelraden blir komplett
        const expectedWeeks = [];
        for (let w = weekRange.start; w <= weekRange.end; w++) {
          const existing = weeks.find(x => x.week === w);
          expectedWeeks.push(existing || { week: w, count: 0 });
        }

        const totalCount = expectedWeeks.reduce((sum, w) => sum + w.count, 0);

        return {
          city,
          weeks: expectedWeeks,
          totalCount,
          avgPerWeek: Math.round(totalCount / expectedWeeks.length)
        };
      });

      const results = await Promise.all(cityPromises);
      const executionTime = Date.now() - startTime;

      results.sort((a, b) => b.totalCount - a.totalCount);

      setComparisonData(results);
      setLastAnalysis({
        symptoms: selectedSymptoms,
        symptomLabel: selectedSymptoms.map(s => tSymptom(s)).join('+'),
        cities: selectedCities.length,
        weekRange: t('regionalComparison.weekRangeLabel', { start: weekRange.start, end: weekRange.end }),
        executionTime,
        apiCalls: selectedCities.length
      });

    } catch (err) {
      console.error('Regional comparison error:', err);
      setError(t('regionalComparison.errorFetch'));
    } finally {
      setLoading(false);
    }
  }, [selectedCities, selectedSymptoms, weekRange, t, tSymptom]);

  // Calculate max for scaling
  const maxCount = useMemo(() => {
    if (!comparisonData) return 0;
    return Math.max(...comparisonData.map(c => c.totalCount), 1);
  }, [comparisonData]);

  return (
    <div className="regional-comparison-dark">
      {/* Header */}
      <div className="regional-header">
        <h2>{t('regionalComparison.title')}</h2>
        <p>{t('regionalComparison.subtitle')}</p>
      </div>

      {/* City Selection */}
      <div className="regional-section">
        <h3 className="section-title">
          <span className="title-icon">🏙️</span>
          {t('regionalComparison.selectCities')}
          <span className="selection-badge">{t('regionalComparison.citiesSelected', { count: selectedCities.length })}</span>
        </h3>
        
        <div className="city-chips">
          {AVAILABLE_CITIES.map(city => {
            const isSelected = selectedCities.includes(city);
            const colorIndex = selectedCities.indexOf(city);
            
            return (
              <button
                key={city}
                className={`city-chip ${isSelected ? 'selected' : ''}`}
                onClick={() => handleCityToggle(city)}
                style={isSelected ? {
                  backgroundColor: CITY_COLORS[colorIndex] + '30',
                  borderColor: CITY_COLORS[colorIndex],
                  color: CITY_COLORS[colorIndex]
                } : {}}
              >
                {isSelected && <span className="chip-index">{colorIndex + 1}</span>}
                {city}
              </button>
            );
          })}
        </div>
        
        <p className="selection-hint">
          {t('regionalComparison.cityHint')}
        </p>
      </div>

      {/* Symptom Selection */}
      <div className="regional-section">
        <h3 className="section-title">
          <span className="title-icon">🔬</span>
          {t('regionalComparison.selectSymptoms')}
          <span className="selection-badge">{t('regionalComparison.symptomsSelected', { count: selectedSymptoms.length })}</span>
        </h3>
        
        <div className="symptom-chips">
          {ALL_SYMPTOMS.map(symptom => {
            const isSelected = selectedSymptoms.includes(symptom);
            
            return (
              <button
                key={symptom}
                className={`symptom-chip ${isSelected ? 'selected' : ''}`}
                onClick={() => handleSymptomToggle(symptom)}
              >
                {tSymptom(symptom)}
              </button>
            );
          })}
        </div>
        
        {selectedSymptoms.length > 0 && (
          <div className="selected-combination">
            <span className="combination-label">{t('regionalComparison.selectedCombination')}</span>
            <span className="combination-value">{selectedSymptoms.map(s => tSymptom(s)).join(' + ')}</span>
          </div>
        )}
      </div>

      {/* Week Selection */}
      <div className="regional-section">
        <h3 className="section-title">
          <span className="title-icon">📅</span>
          {t('regionalComparison.timePeriod')}
        </h3>
        
        <div className="filter-row">
          <div className="filter-group">
            <label>{t('regionalComparison.fromWeek')}</label>
            <select
              className="dark-select"
              value={weekRange.start}
              onChange={(e) => setWeekRange(prev => ({ 
                ...prev, 
                start: parseInt(e.target.value),
                end: Math.max(prev.end, parseInt(e.target.value))
              }))}
            >
              {availableWeeks.map(week => (
                <option key={week} value={week}>{t('regionalComparison.weekOption', { week })}</option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <label>{t('regionalComparison.toWeek')}</label>
            <select
              className="dark-select"
              value={weekRange.end}
              onChange={(e) => setWeekRange(prev => ({ 
                ...prev, 
                end: parseInt(e.target.value),
                start: Math.min(prev.start, parseInt(e.target.value))
              }))}
            >
              {availableWeeks.map(week => (
                <option key={week} value={week}>{t('regionalComparison.weekOption', { week })}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Execute Button */}
      <button
        className={`execute-btn ${loading ? 'loading' : ''}`}
        onClick={executeComparison}
        disabled={loading || selectedCities.length < 2 || selectedSymptoms.length === 0}
      >
        {loading ? (
          <>
            <span className="spinner"></span>
            {t('regionalComparison.analyzing')}
          </>
        ) : selectedSymptoms.length === 0 ? (
          <>
            <span>🔬</span>
            {t('regionalComparison.errorNoSymptoms')}
          </>
        ) : (
          <>
            <span>📊</span>
            {selectedSymptoms.length > 1
              ? t('regionalComparison.btnCompareMultiple', { count: selectedSymptoms.length, cities: selectedCities.length })
              : t('regionalComparison.btnCompareSingle', { symptom: tSymptom(selectedSymptoms[0]), cities: selectedCities.length })
            }
          </>
        )}
      </button>

      {/* Error Display */}
      {error && (
        <div className="regional-error">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      {/* Results */}
      {comparisonData && (
        <div className="regional-section results">
          <h3 className="section-title">
            <span className="title-icon">📊</span>
            {t('regionalComparison.resultsTitle')}: {lastAnalysis?.symptomLabel}
            <span className="period-badge">{lastAnalysis?.weekRange}</span>
          </h3>

          {/* Metadata */}
          <div className="analysis-meta">
            <span className="meta-item">
              <strong>{selectedCities.length}</strong> {t('regionalComparison.metaCities')}
            </span>
            <span className="meta-item">
              <strong>{weekRange.end - weekRange.start + 1}</strong> {t('regionalComparison.metaWeeks')}
            </span>
            <span className="meta-item">
              <strong>{lastAnalysis?.executionTime}ms</strong> ({lastAnalysis?.apiCalls} {t('regionalComparison.metaApiCalls')})
            </span>
          </div>

          {/* Bar Chart */}
          <div className="chart-container">
            <div className="bar-chart">
              {comparisonData.map((cityData, index) => {
                const barWidth = (cityData.totalCount / maxCount) * 100;
                const color = CITY_COLORS[selectedCities.indexOf(cityData.city)] || CITY_COLORS[0];
                
                return (
                  <div key={cityData.city} className="bar-row">
                    <div className="bar-label">
                      <span className="city-rank">#{index + 1}</span>
                      <span className="city-name" style={{ color }}>{cityData.city}</span>
                    </div>
                    <div className="bar-container">
                      <div 
                        className="bar-fill"
                        style={{ 
                          width: `${barWidth}%`,
                          backgroundColor: color,
                          boxShadow: `0 0 20px ${color}50`
                        }}
                      >
                        {barWidth > 15 && (
                          <span className="bar-value">{cityData.totalCount}</span>
                        )}
                      </div>
                      {barWidth <= 15 && (
                        <span className="bar-value-outside">{cityData.totalCount}</span>
                      )}
                    </div>
                    <div className="bar-avg">
                      {t('regionalComparison.avgPerWeek', { avg: cityData.avgPerWeek })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Weekly Breakdown Table */}
          <div className="data-table-container">
            <h4>{t('regionalComparison.weeklyBreakdown')}</h4>
            <div className="table-scroll">
              <table className="regional-table">
                <thead>
                  <tr>
                    <th>{t('regionalComparison.thCity')}</th>
                    {Array.from({ length: weekRange.end - weekRange.start + 1 }, (_, i) => (
                      <th key={i}>{t('regionalComparison.thWeekShort', { week: weekRange.start + i })}</th>
                    ))}
                    <th>{t('regionalComparison.thTotal')}</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.map((cityData) => {
                    const color = CITY_COLORS[selectedCities.indexOf(cityData.city)] || CITY_COLORS[0];
                    
                    return (
                      <tr key={cityData.city}>
                        <td className="city-cell" style={{ color }}>{cityData.city}</td>
                        {cityData.weeks.map((weekData, i) => (
                          <td 
                            key={i} 
                            className={weekData.count > 0 ? 'has-data' : 'no-data'}
                          >
                            {weekData.count || '-'}
                          </td>
                        ))}
                        <td className="total-cell" style={{ color }}>{cityData.totalCount}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Instructions when no data */}
      {!comparisonData && !loading && (
        <div className="regional-instructions">
          <div className="instruction-icon">🗺️</div>
          <h3>{t('regionalComparison.instructionsTitle')}</h3>
          <p>{t('regionalComparison.instructionsText')}</p>
          <div className="instruction-details">
            <span>{t('regionalComparison.stepCities')}</span>
            <span>{t('regionalComparison.stepSymptoms')}</span>
            <span>{t('regionalComparison.stepWeeks')}</span>
            <span>{t('regionalComparison.stepCompare')}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegionalComparison;
